import { components } from "@certn/api/generated-schemas/applicant-portal";
import { PrimaryButton } from "@certn/cascadia";
import { Card, Divider, Stack, Text } from "@certn/cascadia-react";
import { formatCurrency } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

/**
 * NOTE: This component is similar to the client-portal OrderReviewSummaryBox
 * (`apps/client-portal/src/components/ordering-review/OrderReviewSummaryBox.tsx`)
 * but kept separate for now. In the future, these components could be shared
 * or refactored into a common component, but that work is out of scope for
 * the current implementation.
 *
 * Additionally, more work is planned for the applicant-portal payment summary
 * page, so this component may evolve further.
 */

interface PaymentSummaryOrderSummaryProps {
  checks: components["schemas"]["CheckSummary"][];
  totalEstimatedCost: number;
  currency: string;
  disableSubmitButton: boolean;
  onSubmit: () => void;
  region?: string;
}

interface CheckHeaderProps {
  check: components["schemas"]["CheckSummary"];
  currency: string;
}

function CheckHeader({ check, currency }: CheckHeaderProps) {
  return (
    <div className={styles.spaceBetween}>
      <Text
        as="div"
        style="global/heading-small"
        testID={`summary-${check.check_type}-cart`}
      >
        {check.name}
      </Text>
      {check.price !== null && check.price !== undefined && (
        <Text
          as="div"
          style="global/heading-small"
          testID={`summary-${check.check_type}-price`}
        >
          {formatCurrency(check.price, undefined, currency, 2)}
        </Text>
      )}
    </div>
  );
}

export function PaymentSummaryOrderSummary({
  checks,
  totalEstimatedCost,
  currency,
  disableSubmitButton,
  onSubmit,
  region,
}: PaymentSummaryOrderSummaryProps) {
  const { t } = useTranslation();

  return (
    <Card
      type="large"
      className={styles.cardPadding}
      border="dropshadow"
      testID="order-summary-card"
    >
      <div className={styles.innerContainerFlex}>
        <Text style="global/heading-large">{t("Order summary")}</Text>

        {checks.map((check, index) => {
          // If check has child checks, display as a nested structure
          if (check.child_checks && check.child_checks.length > 0) {
            return (
              <div key={check.check_type || index} className={styles.row}>
                <CheckHeader check={check} currency={currency} />
                {check.child_checks.map((child, childIndex) => (
                  <Text
                    key={child.check_type || childIndex}
                    as="div"
                    style="global/metric-detail"
                    color="text-weak"
                    testID={`summary-${child.check_type}-child`}
                  >
                    {child.name}
                  </Text>
                ))}
              </div>
            );
          }

          // Otherwise, display as a simple check item
          return (
            <div key={check.check_type || index} className={styles.row}>
              <CheckHeader check={check} currency={currency} />
            </div>
          );
        })}

        <Divider />

        <Stack className={styles.totals} gap="space-2">
          <div className={styles.spaceBetween}>
            <Text style="form/body-secondary" testID="subtotal-label">
              {t("Subtotal")}
            </Text>
            <Text style="form/body-secondary">
              {formatCurrency(totalEstimatedCost, undefined, currency, 2)}
            </Text>
          </div>
          <div className={styles.spaceBetween}>
            <Text style="global/heading-medium" testID="total-label">
              {t("Total")} {region === "au" && `(${t("ex GST")})`}
            </Text>
            <Text style="global/heading-medium">
              {formatCurrency(totalEstimatedCost, undefined, currency, 2)}
            </Text>
          </div>
          <Text style="global/body-secondary" color="text-weak">
            {t(
              "Payments will only be charged when the checks are complete. Additional pass-through fees may apply."
            )}
          </Text>
        </Stack>

        <Stack className={styles.order} gap="space-2">
          <PrimaryButton
            fullWidth
            isDisabled={disableSubmitButton}
            onPress={onSubmit}
          >
            {t("Submit and pay")}
          </PrimaryButton>
        </Stack>
      </div>
    </Card>
  );
}

const styles = {
  cardPadding: css({
    paddingTop: "var(--space-3)",
    paddingBottom: "var(--space-3)",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
  }),
  innerContainerFlex: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  row: css({
    width: "auto",
  }),
  spaceBetween: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  totals: css({
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
  }),
  order: css({
    paddingBottom: "var(--space-2)",
  }),
};
